// Shared UI primitive (reused across all dashboard pages) — MIS-809.
//
// A small status pill. `variant` picks a color pair (gray/green/yellow/red) used
// for domain/lander lifecycle states. Renders a <span> so it nests inline.

import { cn } from '@/lib/utils';

export type BadgeVariant = 'gray' | 'green' | 'yellow' | 'red';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

// Tones mirror the /ad `Pill` palette (src/ad/theme.ts) so dashboard status
// pills match AI Studio: neutral hairline, teal "success", amber "warning",
// soft-red "danger".
const VARIANTS: Record<BadgeVariant, string> = {
  gray: 'border border-[var(--border-strong)] bg-[rgba(255,255,255,0.06)] text-[var(--muted)]',
  green: 'bg-[rgba(0,212,170,0.1)] text-[var(--accent-light)]',
  yellow: 'bg-[rgba(212,168,83,0.12)] text-[var(--warning)]',
  red: 'bg-[var(--danger-bg)] text-[var(--danger)]',
};

export function Badge({ className, variant = 'gray', ...props }: BadgeProps): React.ReactElement {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
}
